/**
 * MegaMenuIconPreloader - preloads all 3D circle-strip icons in the background
 * after page load so the icons are already in the browser cache by the time
 * the user hovers a mega-menu trigger.
 *
 * Approach: after `window.load`, use `requestIdleCallback` (fallback: setTimeout)
 * to fetch every icon via `new Image()`. Images fetched this way enter the
 * normal HTTP cache, so subsequent renders from the same URLs are instant.
 *
 * Drop this once at the top level (e.g. inside <Header /> via useEffect).
 */
import { useEffect } from 'react';
import { DEPT_ICONS, FEATURED_IMAGES } from './layout/ProductsMegaMenu';

// Builder-type circles (12) - all live under /images/toolbox-icons/builder-types/
const BUILDER_TYPE_SLUGS = [
  'custom', 'volume', 'luxury', 'budget', 'knockdown', 'two-storey',
  'townhouse', 'granny-flat', 'country', 'acreage', 'first-home', 'compare',
];

// Matcher icons used across Suppliers / Trades / Pros circle strips (32 unique)
const MATCHER_SLUGS = [
  // Suppliers
  'kitchens', 'bathrooms', 'tiles', 'flooring', 'stone-benchtops',
  'windows-doors', 'roofing', 'lighting', 'solar-batteries',
  // Trades (shared with matcher icons)
  'electrician', 'plumber', 'painter', 'carpenter', 'tiler',
  'concreter', 'bricklayer', 'plasterer', 'roofer', 'landscaper',
  // Pros
  'architect', 'building-designer', 'interior-designer',
  'building-inspector', 'finance-broker', 'conveyancer',
  'town-planner', 'surveyor', 'structural-engineer',
  'quantity-surveyor', 'property-lawyer',
  'energy-assessor', 'insurance-broker', 'private-certifier',
  // Trades & professionals not covered above
  'air-conditioning', 'pool-builder',
];

const ALL_URLS = [
  ...BUILDER_TYPE_SLUGS.map((s) => `/images/toolbox-icons/builder-types/${s}.webp`),
  ...MATCHER_SLUGS.map((s) => `/images/toolbox-icons/matcher/${s}.webp`),
  // Products mega-menu: department icons + featured lifestyle images, so the
  // first hover/open is instant instead of fetching ~10 assets on demand.
  ...Object.values(DEPT_ICONS),
  ...Object.values(FEATURED_IMAGES),
];

// Probe WebP support once. 96.5% of global traffic supports WebP (caniuse 2026);
// the rare browsers without it skip preload and fall through to the PNG when
// the user actually hovers a mega-menu trigger via SmartIcon's <picture>.
let _supportsWebP = null;
const checkWebP = () => {
  if (_supportsWebP !== null) return _supportsWebP;
  try {
    const c = document.createElement('canvas');
    _supportsWebP = !!(c.toDataURL && c.toDataURL('image/webp').indexOf('image/webp') === 5);
  } catch (e) {
    _supportsWebP = false;
  }
  return _supportsWebP;
};

let _preloadDone = false;

const preload = () => {
  if (_preloadDone || typeof window === 'undefined') return;
  _preloadDone = true;
  const useWebP = checkWebP();
  ALL_URLS.forEach((url) => {
    const finalUrl = useWebP ? url : url.replace(/\.webp$/, '.png');
    const img = new window.Image();
    img.decoding = 'async';
    img.src = finalUrl;
    // Pre-decode so the first paint on hover is instant (no decode flash).
    if (typeof img.decode === 'function') {
      img.decode().catch(() => {});
    }
  });
};

const MegaMenuIconPreloader = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const trigger = () => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(preload, { timeout: 2000 });
      } else {
        // Safari + older browsers
        window.setTimeout(preload, 600);
      }
    };
    if (document.readyState === 'complete') {
      trigger();
    } else {
      window.addEventListener('load', trigger, { once: true });
      return () => window.removeEventListener('load', trigger);
    }
    return undefined;
  }, []);
  return null;
};

export default MegaMenuIconPreloader;
